import { Button } from '@repo/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from '@repo/ui/card'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle
} from '@repo/ui/dialog'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@repo/ui/empty'
import { ScrollArea } from '@repo/ui/scroll-area'
import { Skeleton } from '@repo/ui/skeleton'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { Clock, Lock, TriangleAlert, Unlink } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { warmInstanceBranding } from '@/query/query-options'
import { PasswordForm } from './-components/password-form'
import { PublicHeader } from './-components/public-header'
import { isRelocked, linkAccess, retryMinutes } from './-lib/access'
import { publicLinkQueryOptions } from './-query/query-options'

/**
 * The page a link's recipient opens, and the only page in this app with no
 * session at all.
 *
 * **`/s/$token` because that is the address already handed out.** The
 * share panel builds it (`link-url.ts`), and links carrying
 * it have been sent since MS15; the page comes to the address, not the other
 * way round.
 *
 * **Outside `(private)`, and it reads nothing that needs a session.** No
 * session query, no profile, no app config: the only calls are the four
 * public link routes, which the data plane answers with the token as the
 * whole credential. So there is nothing that could answer `401`, and no
 * redirect to `/login` can fire from here.
 *
 * **`ssr: false`.** The token is the credential, and a server render would
 * carry it through one more server for nothing: a recipient's first paint
 * gains nothing a crawler should index — the head says `noindex` for the
 * crawlers that land here anyway.
 *
 * **This layout owns access; its children own content.** Every state before a
 * file — loading, locked, not available, too many attempts — is decided here
 * from the first read, and the file or folder below renders only once the
 * link is open. When the unlock lapses mid-visit, the password comes back as
 * a dialog over the page rather than in place of it, so a visitor deep in a
 * folder keeps their place.
 */
export const Route = createFileRoute('/s/$token')({
	ssr: false,
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex, nofollow' }]
	}),
	// The header draws the reseller's name and mark.
	loader: ({ context }) => warmInstanceBranding(context.queryClient),
	component: PublicLinkLayout
})

function PublicLinkLayout() {
	const { token } = Route.useParams()
	const read = useQuery(publicLinkQueryOptions(token))
	const access = linkAccess(read)

	return (
		// Held to the viewport, and `main` itself never scrolls: each view owns
		// the one `ScrollArea` below its fixed part — a folder's listing inside
		// its card, a file's preview under the link bar, and the states below
		// here as a whole — so the header and the link bar stay put.
		<div className='flex h-dvh flex-col bg-background'>
			<PublicHeader />
			<main className='flex min-h-0 flex-1 flex-col'>
				{access.state === 'open' ? (
					<>
						<Outlet />
						<RelockDialog token={token} />
					</>
				) : (
					<ScrollArea className='min-h-0 flex-1'>
						<div className='flex min-h-full flex-col'>
							{access.state === 'loading' && <LinkLoading />}
							{access.state === 'locked' && (
								<LinkLocked token={token} />
							)}
							{access.state === 'unavailable' && (
								<LinkUnavailable />
							)}
							{access.state === 'throttled' && (
								<LinkThrottled
									seconds={access.retryAfterSeconds}
								/>
							)}
							{access.state === 'failed' && (
								<LinkFailed onRetry={() => read.refetch()} />
							)}
						</div>
					</ScrollArea>
				)}
			</main>
		</div>
	)
}

/** The skeleton of the file bar, which is the first thing an open link draws. */
function LinkLoading() {
	const { t } = useTranslation()

	return (
		<output
			aria-busy='true'
			aria-label={t('public-link:loading')}
			className='mx-auto flex w-full max-w-5xl items-center gap-3 p-4 sm:p-6'
		>
			<Skeleton className='size-10 shrink-0 rounded-md' />
			<div className='flex flex-1 flex-col gap-2'>
				<Skeleton className='h-4 w-1/2' />
				<Skeleton className='h-3 w-1/3' />
			</div>
		</output>
	)
}

function LinkLocked({ token }: { token: string }) {
	const { t } = useTranslation()

	return (
		<div className='grid flex-1 place-items-center p-4 sm:p-6'>
			<Card className='w-full max-w-sm'>
				{/* `justify-items-center`: the header is a grid, so `items-center`
				    alone left the tile at the start of its row. The tile is the
				    design's — square with rounded corners, brand-tinted, the
				    folder sigil's `bg-accent text-primary` pair. */}
				<CardHeader className='justify-items-center gap-3 text-center'>
					<span className='flex size-14 items-center justify-center rounded-[14px] bg-accent text-primary'>
						<Lock aria-hidden className='size-[26px]' />
					</span>
					<CardTitle>
						<h1>{t('public-link:password.title')}</h1>
					</CardTitle>
					<CardDescription>
						{t('public-link:password.description')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<PasswordForm token={token} />
				</CardContent>
			</Card>
		</div>
	)
}

/**
 * One message for an expired link, a revoked one and a token that never
 * existed. The server answers all three the same on purpose; a page that
 * guessed which one it was would hand a token-guesser the difference.
 */
function LinkUnavailable() {
	const { t } = useTranslation()

	return (
		<Empty className='flex-1'>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Unlink aria-hidden />
				</EmptyMedia>
				<EmptyTitle>
					<h1>{t('public-link:unavailable.title')}</h1>
				</EmptyTitle>
				<EmptyDescription>
					{t('public-link:unavailable.description')}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}

function LinkThrottled({ seconds }: { seconds: number }) {
	const { t } = useTranslation()

	return (
		<Empty className='flex-1'>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Clock aria-hidden />
				</EmptyMedia>
				<EmptyTitle>
					<h1>{t('public-link:throttled.title')}</h1>
				</EmptyTitle>
				<EmptyDescription>
					{t('public-link:throttled.description', {
						count: retryMinutes(seconds)
					})}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}

function LinkFailed({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation()

	return (
		<Empty className='flex-1'>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<TriangleAlert aria-hidden />
				</EmptyMedia>
				<EmptyTitle>
					<h1>{t('public-link:failed.title')}</h1>
				</EmptyTitle>
				<EmptyDescription>
					{t('public-link:failed.description')}
				</EmptyDescription>
			</EmptyHeader>
			<EmptyContent>
				<Button onClick={onRetry} type='button' variant='outline'>
					{t('public-link:failed.retry')}
				</Button>
			</EmptyContent>
		</Empty>
	)
}

/**
 * The password again, over the page, once the unlock lapsed.
 *
 * The lapse is learnt from the reads themselves: the listing and the file URL
 * answer `link_password_required` once ten minutes have passed with no
 * request. The query and mutation caches are the one place every such read
 * reports to, so this subscribes there rather than asking every child to
 * forward its errors up. A successful unlock re-reads everything under the
 * token — the reads that failed included — and the visitor carries on where
 * they were.
 */
function RelockDialog({ token }: { token: string }) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const [relocked, setRelocked] = useState(false)

	useEffect(() => {
		const scope = makeQueryKeys.publicLinks.link(token)
		const inScope = (key: readonly unknown[] | undefined) =>
			key !== undefined &&
			scope.every((part, index) => key[index] === part)

		const unsubscribeQueries = queryClient
			.getQueryCache()
			.subscribe((event) => {
				if (
					event.type === 'updated' &&
					event.action.type === 'error' &&
					inScope(event.query.queryKey) &&
					isRelocked(event.action.error)
				) {
					setRelocked(true)
				}
			})
		const unsubscribeMutations = queryClient
			.getMutationCache()
			.subscribe((event) => {
				if (
					event.type === 'updated' &&
					event.action.type === 'error' &&
					inScope(event.mutation.options.mutationKey) &&
					isRelocked(event.action.error)
				) {
					setRelocked(true)
				}
			})

		return () => {
			unsubscribeQueries()
			unsubscribeMutations()
		}
	}, [queryClient, token])

	return (
		<Dialog onOpenChange={setRelocked} open={relocked}>
			<DialogContent className='sm:max-w-sm'>
				<DialogHeader>
					<DialogTitle>{t('public-link:password.title')}</DialogTitle>
					<DialogDescription>
						{t('public-link:relock.description')}
					</DialogDescription>
				</DialogHeader>
				<PasswordForm
					onUnlocked={() => setRelocked(false)}
					token={token}
				/>
			</DialogContent>
		</Dialog>
	)
}
